/**
 * ORO Mutation Wrapper
 * Provides automatic error toasting and retry logic for mutations
 * 
 * Phase A: Error toasts only (no retry)
 * Phase C: Selective retry for idempotent operations
 */

import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getUserFacingError as getCanonicalError } from '../lib/errorMessages';
import { ACCOUNT_MISMATCH_CODE } from '../lib/accountGuard';
import { useRef } from 'react';

/**
 * Error shape types for the adapter
 * Supports multiple error formats during API transition
 * Exported for use in onError handlers across the app
 */
export interface ApiErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
  code?: string;
  message?: string;
  status?: number;
  name?: string;
}

/**
 * Adapter: Bridges #708 toast interface with #657 canonical error messages
 *
 * Handles multiple error shapes:
 * 1. Standardized envelope (post-#657): { error: { code, message, details? } }
 * 2. Legacy flat format: { code, message, status }
 * 3. Raw status codes: { status: 404 }
 *
 * Frank: Fallback logic for mixed error formats during transition period
 */
function adaptErrorToToast(error: ApiErrorEnvelope): { title: string; description?: string } {
  // Extract error code and message from various shapes
  // Priority: standardized envelope > flat format > fallback
  const rawCode = error?.error?.code || error?.code || `HTTP_${error?.status || 500}`;
  // Ensure code is a string (could be number from HTTP status)
  const code = typeof rawCode === 'string' ? rawCode : String(rawCode);
  let message = error?.error?.message || error?.message || 'An unexpected error occurred';

  // Normalize legacy lowercase codes to SCREAMING_SNAKE_CASE
  // Frank: Temporary during transition period (found 3 legacy codes in backend)
  const normalizedCode = code.toUpperCase().replace(/-/g, '_');

  // #1369: For validation errors, extract the first field error message
  // This gives users actionable feedback like "Secret word is required"
  if (normalizedCode === 'VALIDATION_ERROR') {
    const details = error?.error?.details as { field_errors?: Array<{ field: string; message: string }> } | undefined;
    if (details?.field_errors?.length) {
      // Use the first field error message - it's usually the most relevant
      message = details.field_errors[0].message;
    }
  }

  // Get canonical user-facing message
  const userMessage = getCanonicalError(normalizedCode, message);

  // Frank: Safer title/description split
  // Keep full message as title, no splitting to avoid abbreviation issues
  // Future: errorMessages.ts could return structured objects
  return {
    title: userMessage,
    description: undefined  // Keep it simple - single message for toasts
  };
}

/**
 * Build the de-dup id for a mutation error toast. Exported as the single source
 * of truth so a call site that wants to OVERRIDE the wrapper's toast in place
 * (e.g. #1445 ClubChatPage swapping the generic RATE_LIMITED copy for the
 * server's specific announcement-cap message) can target the exact same id
 * without hard-coding the format — if this format changes, both move together.
 */
export function oroErrorToastId(
  op: string,
  status: number | string | undefined,
  code: string | undefined,
): string {
  return `oro-${op}-${status ?? 'unknown'}-${code ?? 'generic'}`;
}

/**
 * Type-safe operation identifiers (George: prevents string typos)
 * Frank: Plain object with `as const` for better tree-shaking and runtime logging
 * Grouped by domain for maintainability
 */
export const OroOperation = {
  // Clubs
  CreateClub: 'createClub',
  UpdateClub: 'updateClub',
  DeleteClub: 'deleteClub',
  JoinClub: 'joinClub',
  LeaveClub: 'leaveClub',
  TransferClubOwnership: 'transferClubOwnership',
  RequestMembership: 'requestMembership', // #782
  ReviewMembershipRequest: 'reviewMembershipRequest', // #1239

  // Events
  CreateEvent: 'createEvent',
  UpdateEvent: 'updateEvent',
  DeleteEvent: 'deleteEvent',
  UploadEventImage: 'uploadEventImage',
  RemoveEventImage: 'removeEventImage',

  // Images (#792)
  UploadImage: 'uploadImage',

  // Event Routes (Issue #579)
  UploadEventRoute: 'uploadEventRoute',
  RemoveEventRoute: 'removeEventRoute',

  // RSVPs & Check-ins
  UpdateRSVP: 'updateRSVP',       // Covers create/update/delete for RSVPs
  CheckIn: 'checkIn',

  // Messages
  // SendMessage ('sendMessage') removed with the dead club-scoped send route (#2666).
  SendRoomMessage: 'sendRoomMessage',  // Room-based messaging (#790)
  ToggleReaction: 'toggleReaction',    // #989 emoji reactions
  PinMessage: 'pinMessage',            // #2708 pin a message
  UnpinMessage: 'unpinMessage',        // #2708 unpin

  // Profile
  UpdateProfile: 'updateProfile',
  UploadProfileImage: 'uploadProfileImage',
  RemoveProfileImage: 'removeProfileImage',

  // Account self-service GDPR (#531, #532)
  ExportMyData: 'exportMyData',
  RequestAccountDeletion: 'requestAccountDeletion',
  ConfirmAccountDeletion: 'confirmAccountDeletion',

  // Members
  UpdateMemberRole: 'updateMemberRole',
  RemoveMember: 'removeMember',

  // Notifications
  UpdateNotificationPreferences: 'updateNotificationPreferences',
  SendTestNotification: 'sendTestNotification',

  // Phone Verification (#1461)
  RequestPhoneVerification: 'requestPhoneVerification',
  VerifyPhone: 'verifyPhone',
  RemovePhone: 'removePhone',

  // Add-Email Recovery (#2930) — phone-only STOP lockout
  RequestEmailVerification: 'requestEmailVerification',
  VerifyEmail: 'verifyEmail',

  // Waitlist
  JoinWaitlist: 'joinWaitlist',

  // Training Groups (Issue #727)
  CreateTrainingGroup: 'createTrainingGroup',
  UpdateTrainingGroup: 'updateTrainingGroup',
  DeleteTrainingGroup: 'deleteTrainingGroup',
  JoinTrainingGroup: 'joinTrainingGroup',
  LeaveTrainingGroup: 'leaveTrainingGroup',

  // Admin Reports
  UpdateReport: 'updateReport',
  DeleteReportedMessage: 'deleteReportedMessage',
  SuspendUser: 'suspendUser', // #3260 — admin eject from a report

  // Invites
  AcceptInvite: 'acceptInvite',
  DeclineInvite: 'declineInvite',  // #907
  GetClubShareLink: 'getClubShareLink', // #3561 Bring a friend

  // Contact
  SubmitContactForm: 'submitContactForm',

  // Calendar Feed (#941, #943)
  RegenerateCalendarFeed: 'regenerateCalendarFeed',
  UpdateCalendarFeedMode: 'updateCalendarFeedMode',

  // Club Calendar Feed (#1058)
  RegenerateClubCalendarFeed: 'regenerateClubCalendarFeed',

  // Moderation (#920)
  BlockUser: 'blockUser',
  UnblockUser: 'unblockUser',
  ReportUser: 'reportUser',
  DeleteMessage: 'deleteMessage',

  // Pace Groups (#541 - v1.2)
  CreatePaceGroup: 'createPaceGroup',
  UpdatePaceGroup: 'updatePaceGroup',
  DeletePaceGroup: 'deletePaceGroup',
} as const;

export type OroOperation = (typeof OroOperation)[keyof typeof OroOperation];

interface OroMutationOptions<TData, TVariables, TContext = unknown> extends Omit<UseMutationOptions<TData, ApiErrorEnvelope, TVariables, TContext>, 'mutationFn'> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successToast?: string | ((data: TData) => string);
  oro: {
    op: OroOperation; // Required: type-safe operation identifier
    retryPolicy?: 'safe' | 'never'; // Phase C: retry policy
    requiresIdempotency?: boolean;  // Phase C: idempotency flag
    debugLabel?: string; // Dev-only: appends to toast description (Frank)
    /**
     * Opt-IN error-toast suppression for INLINE-OWNED errors (#2989).
     *
     * Default (undefined/false) preserves the existing unconditional
     * `toast.error` — no existing consumer changes behavior. Set `true` ONLY
     * when the call site renders the error itself (e.g. AddEmailSection shows
     * the backend's exact per-branch copy inline). Otherwise the wrapper's
     * generic toast fires ALONGSIDE the precise inline message and the generic
     * one is the louder of the two — the exact ambiguity #2930's add-email
     * recovery flow exists to remove. The user's `onError` callback and the
     * thrown rejection still run; only the wrapper-owned `toast.error` is
     * silenced. Do NOT use this to silence errors that have no inline surface.
     */
    suppressErrorToast?: boolean;
  };
}

/**
 * ORO mutation wrapper with automatic error toasting
 * Phase A: Errors only (no retry actions)
 * Phase C: Selective retry for idempotent operations
 * 
 * @example
 * const updateProfile = useOroMutation({
 *   mutationFn: (data) => apiPost('/me', data),
 *   successToast: 'Profile updated',
 *   oro: { op: OroOperation.UpdateProfile, retryPolicy: 'never' },
 *   onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
 * });
 */
export function useOroMutation<TData = unknown, TVariables = unknown, TContext = unknown>({
  mutationFn,
  successToast,
  oro,
  onSuccess,
  onError,
  onSettled,
  ...options
}: OroMutationOptions<TData, TVariables, TContext>) {
  const lastVariables = useRef<TVariables | null>(null);

  // Dev-time safety check (George)
  if (import.meta.env.DEV) {
    if (oro.retryPolicy === 'safe' && !oro.requiresIdempotency) {
      console.warn(
        `[useOroMutation] ${oro.op} has retryPolicy='safe' but requiresIdempotency is false. ` +
        'This could cause duplicates. Set requiresIdempotency: true or retryPolicy: "never".'
      );
    }
  }

  const mutation = useMutation<TData, ApiErrorEnvelope, TVariables, TContext>({
    mutationFn,
    ...options,
    // Type assertion needed due to TanStack Query v5 callback signature differences
    onMutate: (async (variables: TVariables) => {
      lastVariables.current = variables; // Capture for retry
      // Call user's onMutate if provided
      if (options.onMutate) {
        return (options.onMutate as (v: TVariables) => TContext | Promise<TContext> | undefined)(variables);
      }
      return undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as any, // TanStack Query v5 callback type compatibility
    onSuccess: (data, variables, context) => {
      lastVariables.current = null; // Clear on success (Frank)
      // Call user's onSuccess with standard 3-arg signature
      if (onSuccess) {
        (onSuccess as (data: TData, variables: TVariables, context: TContext | undefined) => void)(data, variables, context);
      }

      // Show success toast if provided
      if (successToast) {
        const message = typeof successToast === 'string'
          ? successToast
          : successToast(data);
        toast.success(message, { duration: 3000 });
      }
    },
    onError: (error: ApiErrorEnvelope, variables, context) => {
      // Call user's onError with standard 3-arg signature
      if (onError) {
        (onError as (error: ApiErrorEnvelope, variables: TVariables, context: TContext | undefined) => void)(error, variables, context);
      }
      
      // Skip abort errors (navigation cancellations) - Frank
      // Note: Verify these match your API client's actual error names
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return;
      }
      
      // Defer to global 401 handler (silent - no toast, no log) - Frank
      if (error?.status === 401) {
        return;
      }

      // #3881: the account guard already told the user and is reloading the tab.
      if (error?.code === ACCOUNT_MISMATCH_CODE) {
        return;
      }

      // #2989: inline-owned errors — the call site renders the message itself,
      // so the wrapper must NOT also fire a toast (generic toast + precise
      // inline message = the ambiguity #2930 removes). Opt-IN; runs AFTER the
      // user's onError so the call site still receives the error. Suppresses
      // BOTH the malformed-shape and normal toast branches below.
      if (oro.suppressErrorToast) {
        return;
      }

      // Handle unexpected error shapes (George: safety net for malformed errors)
      if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
        if (import.meta.env.DEV) {
          console.warn('[useOroMutation] Unexpected error shape:', error);
        }
        toast.error('Unexpected error', {
          description: 'Please try again or contact support',
          id: `oro-${oro.op}-malformed`,
          duration: 5000,
        });
        return;
      }
      
      // Get user-facing error message
      const userError = adaptErrorToToast(error);
      
      // Deduplicate by operation + status + code (Frank: prevents unrelated errors from deduping)
      const toastId = oroErrorToastId(oro.op, error?.status, error?.code);
      
      // Add debug context in development (Frank: helps QA)
      const description = import.meta.env.DEV && oro.debugLabel
        ? `${userError.description}\n\n[Debug: ${oro.debugLabel}]`
        : userError.description;
      
      // Phase A: Toast error (NO retry action)
      toast.error(userError.title, {
        description,
        id: toastId,
        duration: 5000,
      });
    },
    onSettled: (data, error, variables, context) => {
      lastVariables.current = null; // Clear on settled (Frank)
      // #1764: compose user-supplied onSettled (previously dropped silently).
      // Frank (P2): RETURN the user callback's result so TanStack Query can
      // await it. query-core mutation.js L135 (success) / L179 (error) does
      // `await this.options.onSettled?.(...)` — consumers that do
      // `return queryClient.invalidateQueries(...)` rely on the mutation
      // staying `isPending` until invalidation resolves. Cast preserves the
      // wrapper's 4-arg public surface (v5 adds a 5th MutationFunctionContext
      // arg internally that we don't expose to OroMutationOptions consumers)
      // while keeping the `Promise<unknown> | unknown` return type intact.
      return (
        onSettled as
          | ((
              data: TData | undefined,
              error: ApiErrorEnvelope | null,
              variables: TVariables,
              context: TContext | undefined,
            ) => Promise<unknown> | unknown)
          | undefined
      )?.(data, error, variables, context);
    },
  });

  return mutation;
}
